import {
  DashboardFilters,
  ConversationCountResponse,
  FeedbackSummaryResponse,
  FeedbackOverTimeResponse,
  QuestionsByPlanItem,
  TopQuestionItem,
  ConversationDetail,
  NegativeFeedbackItem,
  ActiveUsersResponse,
  ConversationsOverTimeResponse,
  Plan,
  UserFilterOption,
  LLMSummaryResponse,
  LLMReportInfo,
  LLMReportResponse,
  PerformanceSummaryResponse,
  SlowestAnswersResponse,
  RecentMessagesResponse,
  QuarterlySubscribersResponse
} from './models'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
const CLIENTS_ONLY_KEY = '__clients_only__'

// Data environment selection (production / staging). Persisted in localStorage
// and appended to every API request so the backend reads from the matching
// Cosmos DB / SQL Server / blob storage.
export const ENV_STORAGE_KEY = 'cemq_environment'
export const DEFAULT_ENVIRONMENT = 'production'
export const ENVIRONMENTS: { key: string; text: string }[] = [
  { key: 'production', text: 'Production' },
  { key: 'staging', text: 'Staging' },
]

export function getEnvironment(): string {
  if (typeof localStorage === 'undefined') return DEFAULT_ENVIRONMENT
  return localStorage.getItem(ENV_STORAGE_KEY) || DEFAULT_ENVIRONMENT
}

export function setEnvironment(env: string): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(ENV_STORAGE_KEY, env)
  }
}

function withEnv(url: string): string {
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}env=${encodeURIComponent(getEnvironment())}`
}

// Token getter - set by ProtectedRoute when auth is ready
let _getToken: (() => string | null) | null = null

export function setTokenGetter(fn: () => string | null) {
  _getToken = fn
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> || {}) }
  if (_getToken) {
    const token = _getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }
  return fetch(withEnv(url), { ...init, headers })
}

function buildQueryParams(filters: DashboardFilters): string {
  const params = new URLSearchParams()
  if (filters.startDate) params.append('start_date', filters.startDate)
  if (filters.endDate) params.append('end_date', filters.endDate)
  if (filters.planId !== undefined && filters.planId !== null) params.append('plan_id', filters.planId.toString())
  if (filters.userId && filters.userId !== CLIENTS_ONLY_KEY) params.append('user_id', filters.userId)
  if (filters.userId === CLIENTS_ONLY_KEY) params.append('exclude_cem', 'true')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export async function getConversationCount(filters: DashboardFilters): Promise<ConversationCountResponse> {
  const response = await authFetch(`${API_BASE}/api/analytics/conversations/count${buildQueryParams(filters)}`)
  if (!response.ok) throw new Error('Failed to fetch conversation count')
  return response.json()
}

export async function getFeedbackSummary(filters: DashboardFilters): Promise<FeedbackSummaryResponse> {
  const response = await authFetch(`${API_BASE}/api/analytics/feedback/summary${buildQueryParams(filters)}`)
  if (!response.ok) throw new Error('Failed to fetch feedback summary')
  return response.json()
}

export async function getActiveUsers(filters: DashboardFilters): Promise<ActiveUsersResponse> {
  const response = await authFetch(`${API_BASE}/api/analytics/users/active${buildQueryParams(filters)}`)
  if (!response.ok) throw new Error('Failed to fetch active users')
  return response.json()
}

export async function getConversationsTimeline(
  filters: DashboardFilters,
  granularity: string = 'day'
): Promise<ConversationsOverTimeResponse> {
  const params = new URLSearchParams()
  if (filters.startDate) params.append('start_date', filters.startDate)
  if (filters.endDate) params.append('end_date', filters.endDate)
  if (filters.planId !== undefined && filters.planId !== null) params.append('plan_id', filters.planId.toString())
  if (filters.userId && filters.userId !== CLIENTS_ONLY_KEY) params.append('user_id', filters.userId)
  if (filters.userId === CLIENTS_ONLY_KEY) params.append('exclude_cem', 'true')
  params.append('granularity', granularity)
  const response = await authFetch(`${API_BASE}/api/analytics/conversations/timeline?${params}`)
  if (!response.ok) throw new Error('Failed to fetch timeline')
  return response.json()
}

export async function getFeedbackOverTime(filters: DashboardFilters): Promise<FeedbackOverTimeResponse> {
  const response = await authFetch(`${API_BASE}/api/analytics/feedback/over-time${buildQueryParams(filters)}`)
  if (!response.ok) throw new Error('Failed to fetch feedback over time')
  return response.json()
}

export async function getQuestionsByPlan(filters: DashboardFilters): Promise<QuestionsByPlanItem[]> {
  const response = await authFetch(`${API_BASE}/api/analytics/questions/by-plan${buildQueryParams(filters)}`)
  if (!response.ok) throw new Error('Failed to fetch questions by plan')
  return response.json()
}

export async function getTopQuestions(filters: DashboardFilters): Promise<TopQuestionItem[]> {
  const response = await authFetch(`${API_BASE}/api/analytics/questions/top${buildQueryParams(filters)}`)
  if (!response.ok) throw new Error('Failed to fetch top questions')
  return response.json()
}

export async function getPerformanceSummary(
  filters: DashboardFilters,
  signal?: AbortSignal
): Promise<PerformanceSummaryResponse> {
  const response = await authFetch(`${API_BASE}/api/analytics/performance/summary${buildQueryParams(filters)}`, { signal })
  if (!response.ok) throw new Error('Failed to fetch performance summary')
  return response.json()
}

export async function getSlowestAnswers(
  filters: DashboardFilters,
  limit: number = 10,
  signal?: AbortSignal
): Promise<SlowestAnswersResponse> {
  const params = new URLSearchParams(buildQueryParams(filters))
  params.append('limit', limit.toString())
  const response = await authFetch(`${API_BASE}/api/analytics/performance/slowest?${params}`, { signal })
  if (!response.ok) throw new Error('Failed to fetch slowest answers')
  return response.json()
}

export async function getRecentMessages(
  filters: DashboardFilters,
  limit: number = 10,
  signal?: AbortSignal
): Promise<RecentMessagesResponse> {
  const params = new URLSearchParams(buildQueryParams(filters))
  params.append('limit', limit.toString())
  const response = await authFetch(`${API_BASE}/api/analytics/performance/recent-messages?${params}`, { signal })
  if (!response.ok) throw new Error('Failed to fetch recent messages')
  return response.json()
}

export async function getQuarterlySubscribers(signal?: AbortSignal): Promise<QuarterlySubscribersResponse> {
  const response = await authFetch(`${API_BASE}/api/analytics/kr/quarterly-subscribers`, { signal })
  if (!response.ok) throw new Error('Failed to fetch quarterly subscriber KR')
  return response.json()
}

export async function getConversation(conversationId: string, signal?: AbortSignal): Promise<ConversationDetail | null> {
  const response = await authFetch(`${API_BASE}/api/analytics/conversations/${encodeURIComponent(conversationId)}`, { signal })
  if (response.status === 404) return null
  if (!response.ok) throw new Error('Failed to fetch conversation')
  return response.json()
}

export async function getConversationsList(filters: DashboardFilters): Promise<ConversationDetail[]> {
  const response = await authFetch(`${API_BASE}/api/analytics/conversations/list${buildQueryParams(filters)}`)
  if (!response.ok) throw new Error('Failed to fetch conversations')
  return response.json()
}

export async function getFeedbackDetails(
  filters: DashboardFilters,
  feedbackType?: string
): Promise<NegativeFeedbackItem[]> {
  const params = new URLSearchParams()
  if (filters.startDate) params.append('start_date', filters.startDate)
  if (filters.endDate) params.append('end_date', filters.endDate)
  if (filters.planId !== undefined && filters.planId !== null) params.append('plan_id', filters.planId.toString())
  if (filters.userId && filters.userId !== CLIENTS_ONLY_KEY) params.append('user_id', filters.userId)
  if (filters.userId === CLIENTS_ONLY_KEY) params.append('exclude_cem', 'true')
  if (feedbackType) params.append('feedback_type', feedbackType)
  const qs = params.toString()
  const response = await authFetch(`${API_BASE}/api/analytics/feedback/details${qs ? `?${qs}` : ''}`)
  if (!response.ok) throw new Error('Failed to fetch feedback')
  return response.json()
}

export async function getFilterPlans(): Promise<Plan[]> {
  const response = await authFetch(`${API_BASE}/api/analytics/filters/plans`)
  if (!response.ok) throw new Error('Failed to fetch plans')
  return response.json()
}

export async function getFilterUsers(): Promise<UserFilterOption[]> {
  const response = await authFetch(`${API_BASE}/api/analytics/filters/users`)
  if (!response.ok) throw new Error('Failed to fetch users')
  return response.json()
}

// LLM Results
export async function getLLMReports(): Promise<LLMReportInfo[]> {
  const response = await authFetch(`${API_BASE}/api/llm-results/reports`)
  if (!response.ok) throw new Error('Failed to fetch reports')
  return response.json()
}

export async function getLLMSummary(startDate?: string, endDate?: string): Promise<LLMSummaryResponse> {
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  const qs = params.toString()
  const response = await authFetch(`${API_BASE}/api/llm-results/summary${qs ? `?${qs}` : ''}`)
  if (!response.ok) throw new Error('Failed to fetch LLM summary')
  return response.json()
}

export async function getLLMReport(date: string): Promise<LLMReportResponse> {
  const response = await authFetch(`${API_BASE}/api/llm-results/report/${date}`)
  if (!response.ok) throw new Error('Failed to fetch LLM report')
  return response.json()
}
